import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Download, TrendingUp } from "lucide-react";
import { useId, useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  descargarB2bUtilidadXlsx,
  getB2bGrupos,
  getB2bUtilidad,
  getMemDetalle,
  getTbfCenace,
  type B2bFuenteMov,
  type B2bUtilidadContrato,
} from "@/lib/api";

// Misma paleta que el Reporte de Inventario; tonos tenues para la proyección.
// "Ingresos" (ámbar) = ventas bilaterales + abonos del MEM; "Egresos" (índigo)
// = compras bilaterales + cargos del MEM (en energía el flujo va invertido al rol).
const COLOR_EGRESOS = "#4f46e5"; // índigo
const COLOR_INGRESOS = "#f59e0b"; // ámbar
const COLOR_UTILIDAD = "#059669"; // esmeralda
const COLOR_EGRESOS_PROY = "#c7d2fe";
const COLOR_INGRESOS_PROY = "#fde68a";
const COLOR_UTILIDAD_PROY = "#6ee7b7";
const COLOR_ACUM = "#7c3aed"; // violeta (utilidad acumulada, eje derecho)
const PALETA = [
  "#4f46e5", "#0ea5e9", "#14b8a6", "#f59e0b", "#ef4444", "#8b5cf6",
  "#ec4899", "#22c55e", "#eab308", "#06b6d4", "#a855f7", "#f97316",
];

const nf = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 });
const money = (n: number | null | undefined, moneda = "USD", dec = 0) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: moneda === "MXN" ? "MXN" : "USD",
        maximumFractionDigits: dec,
        minimumFractionDigits: dec,
      }).format(n);

const UNIDAD_MARGEN: Record<string, string> = {
  Potencia: "/MW-año calzado",
  CEL: "/CEL calzado",
  "i-REC": "/i-REC calzado",
  Energía: "/MWh liquidado",
};

// Chips de producto (mismos colores que /contratos y el Reporte de Inventario).
const PRODUCTO_UI: Record<string, { on: string }> = {
  "Energía": { on: "bg-amber-100 text-amber-800 border-amber-300" },
  "Potencia": { on: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  "CEL": { on: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  "i-REC": { on: "bg-sky-100 text-sky-800 border-sky-300" },
};

export function B2bUtilidadPage() {
  // Defaults pedidos por la mesa: vista mensual en MXN.
  const [agrupacion, setAgrupacion] = useState<"anual" | "mensual">("mensual");
  const [moneda, setMoneda] = useState<"USD" | "MXN">("MXN");
  // "Incluir Proyecciones" (default ON) gobierna TODA la proyección: apagado =
  // solo montos en firme (fuera las cargadas de /proyecciones Y las de ficha,
  // p. ej. Xoxocotla/QTX — corrección del usuario 2026-09-01).
  const [incluirProyecciones, setIncluirProyecciones] = useState(true);
  const mostrarProy = incluirProyecciones;
  // Tope MENSUAL de la proyección (YYYY-MM): recorta lo proyectado (cargadas y
  // de ficha) después de ese mes; vacío = sin tope. Default: diciembre del año
  // en curso (pedido del usuario 2026-09-01).
  const [proyHasta, setProyHasta] = useState(() => `${new Date().getFullYear()}-12`);
  // el backend recibe una fecha: el último día del mes elegido (tope inclusive)
  const proyHastaFecha = useMemo(() => {
    if (!/^\d{4}-\d{2}$/.test(proyHasta)) return undefined;
    const [y, m] = proyHasta.split("-").map(Number);
    return `${proyHasta}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
  }, [proyHasta]);
  // "Iniciar el corte en" (sustituyó al checkbox "Incluir contratos terminados",
  // 2026-09-03): solo entran los movimientos DESDE ese año/mes; vacío = todo.
  // Los futuros siempre entran (se quitó el radio Solo vigentes/Incluir futuros).
  const [corteDesde, setCorteDesde] = useState(""); // YYYY-MM
  const [porContraparte, setPorContraparte] = useState(false);
  // Pata bilateral de energía SIEMPRE en modo documentos (facturas/sombras +
  // fantasma del motor); se quitó el checkbox "Respaldo de TBFin".
  const respaldoTbfin = false;
  // Zoom de la gráfica: rango Desde/Hasta (YYYY-MM) que acota los buckets
  // mostrados (gráfica + columnas del desglose); vacío = todo.
  const [zoomDesde, setZoomDesde] = useState("");
  const [zoomHasta, setZoomHasta] = useState("");
  // TC de conversión CAPTURABLE (pedido 2026-09-02, default 17.5000, 4 dec):
  // se aplica al salir del campo o con Enter (no en cada tecla — la consulta
  // es pesada). Vaciar el campo vuelve al FIX de Banxico.
  const [tcStr, setTcStr] = useState("17.5000");
  const [tcAplicado, setTcAplicado] = useState<number | undefined>(17.5);
  const aplicarTc = () => {
    const v = Number(tcStr);
    if (tcStr.trim() === "") setTcAplicado(undefined);
    else if (Number.isFinite(v) && v > 0) setTcAplicado(Math.round(v * 10000) / 10000);
  };
  // Acumular: las barras/líneas de ingresos y egresos muestran el corrido
  // (suma acumulada hasta cada periodo) en vez del monto del periodo.
  const [acumular, setAcumular] = useState(false);
  // "Mostrar transacciones con" — fuente del movimiento: CENACE (liquidaciones
  // del estado de cuenta — pata MEM) vs Contraparte (fuera del edocta: strike,
  // potencia, CEL, proyecciones). Combinables; deseleccionar ambos re-selecciona
  // ambos (pedido 2026-09-01). Desde 2026-09-07 filtra TODO el reporte (KPIs,
  // gráfica, detalle y Excel): viaja al backend como `fuente`, ya no recorta la
  // tabla en el cliente.
  const [verCenace, setVerCenace] = useState(true);
  const [verContraparte, setVerContraparte] = useState(true);
  const fuenteMov: B2bFuenteMov =
    verCenace && verContraparte ? "ambas" : verCenace ? "cenace" : "contraparte";
  const toggleFuente = (cual: "cenace" | "contraparte") => {
    const c = cual === "cenace" ? !verCenace : verCenace;
    const p = cual === "contraparte" ? !verContraparte : verContraparte;
    if (!c && !p) {
      setVerCenace(true);
      setVerContraparte(true);
    } else {
      setVerCenace(c);
      setVerContraparte(p);
    }
  };
  // Productos EXCLUIDOS de la gráfica (vacío = todos). Se guarda la exclusión y
  // no la selección para que un producto nuevo entre visible por default.
  const [prodExcl, setProdExcl] = useState<Set<string>>(new Set());
  // Filtro por grupo back-to-back (los definidos en /b2b); null = cartera completa.
  const [grupo, setGrupo] = useState<number | null>(null);
  // Descarga del Detalle por contrato como Excel (mismos parámetros del dashboard).
  const [bajandoXlsx, setBajandoXlsx] = useState(false);
  const [errorXlsx, setErrorXlsx] = useState<string | null>(null);
  const etapas = { incluirFuturos: true, corteDesde: corteDesde ? `${corteDesde}-01` : undefined };
  const descargarXlsx = async () => {
    setBajandoXlsx(true);
    setErrorXlsx(null);
    try {
      await descargarB2bUtilidadXlsx(agrupacion, moneda, respaldoTbfin, grupo, etapas,
        incluirProyecciones, proyHastaFecha, tcAplicado, fuenteMov);
    } catch (e) {
      setErrorXlsx(e instanceof Error ? e.message : "No se pudo generar el Excel");
    } finally {
      setBajandoXlsx(false);
    }
  };

  const gruposQ = useQuery({ queryKey: ["b2b-grupos"], queryFn: getB2bGrupos });
  const grupos = gruposQ.data?.items ?? [];
  // Cobranza vía TBF (CENACE): informativa, va debajo del detalle.
  const tbfQ = useQuery({ queryKey: ["b2b-tbf-cenace"], queryFn: getTbfCenace });
  // Modal con el detalle diario de la pata MEM (clic al monto de un mes/año
  // en las filas abono/cargo del MEM); periodo = la columna clicada.
  const [memSel, setMemSel] = useState<{ c: B2bUtilidadContrato; periodo?: string } | null>(null);

  const q = useQuery({
    queryKey: ["b2b-utilidad", agrupacion, moneda, grupo,
      corteDesde, incluirProyecciones, proyHastaFecha, tcAplicado, fuenteMov],
    queryFn: () => getB2bUtilidad(agrupacion, moneda, respaldoTbfin, grupo, etapas,
      incluirProyecciones, proyHastaFecha, tcAplicado, fuenteMov),
  });

  const fx = q.data?.fx;
  const productos = q.data?.por_producto ?? [];
  const seriesCp = q.data?.series ?? [];
  const contratos = q.data?.contratos ?? [];
  const noCalculables = contratos.filter((c) => !c.calculable);

  const contrapartes = useMemo(
    () => Array.from(new Set(seriesCp.map((s) => s.contraparte))),
    [seriesCp],
  );
  const colorDe = (cp: string) => PALETA[Math.max(0, contrapartes.indexOf(cp)) % PALETA.length];

  // Productos disponibles y series que pasan el filtro.
  const productosDisp = useMemo(
    () => Array.from(new Set(productos.map((p) => p.producto))).sort(),
    [productos],
  );
  const activo = (p: string) => !prodExcl.has(p);
  const seriesVis = useMemo(
    () => seriesCp.filter((s) => !prodExcl.has(s.producto)),
    [seriesCp, prodExcl],
  );
  // En "por contraparte" se agrupan las series (una por contraparte+lado) para
  // que la leyenda no repita una entrada por producto.
  const gruposCp = useMemo(() => {
    const m = new Map<string, { key: string; contraparte: string; lado: string; fuentes: string[] }>();
    for (const s of seriesVis) {
      const gk = `${s.contraparte}||${s.lado}`;
      if (!m.has(gk))
        m.set(gk, { key: `g${m.size}`, contraparte: s.contraparte, lado: s.lado, fuentes: [] });
      m.get(gk)!.fuentes.push(s.key);
    }
    return [...m.values()];
  }, [seriesVis]);

  const buckets = useMemo(() => {
    const bs = q.data?.buckets ?? [];
    const filas = bs.map((b) => {
      const num = (k: string) => (typeof b[k] === "number" ? (b[k] as number) : 0);
      // Totales recalculados SOLO con los productos visibles.
      let vr = 0, vp = 0, cr = 0, cp = 0;
      for (const s of seriesVis) {
        const r = num(`${s.key}_real`);
        const p = num(`${s.key}_proy`);
        if (s.lado === "ventas") { vr += r; vp += p; } else { cr += r; cp += p; }
      }
      const porGrupo: Record<string, number> = {};
      for (const g of gruposCp) {
        porGrupo[`${g.key}_disp`] = g.fuentes.reduce(
          (s, k) => s + num(`${k}_real`) + (mostrarProy ? num(`${k}_proy`) : 0), 0);
      }
      return {
        ...b,
        ...porGrupo,
        ventas_real: vr, ventas_proy: vp, compras_real: cr, compras_proy: cp,
        compras_disp: cr + (mostrarProy ? cp : 0),
        ventas_disp: vr + (mostrarProy ? vp : 0),
        proyectado: vr === 0 && cr === 0,
        utilidad_real_linea: vr === 0 && cr === 0 ? null : vr - cr,
        utilidad_proy_linea: vp > 0 || cp > 0 ? vp - cp : null,
        // serie invisible: solo alimenta el renglón "en firme + proyección"
        // del tooltip (se dibuja con opacidad 0 cuando la proyección está activa)
        utilidad_total_linea:
          vr === 0 && cr === 0 && vp === 0 && cp === 0 ? null : vr - cr + (vp - cp),
      };
    });
    // Zoom Desde/Hasta (YYYY-MM): acota los periodos visibles. Los buckets
    // anuales se comparan por año; los mensuales por la etiqueta completa
    // (comparación de texto: el formato YYYY[-MM] ordena solo).
    const enZoom = (et: string) => {
      const d = zoomDesde ? (agrupacion === "anual" ? zoomDesde.slice(0, 4) : zoomDesde) : null;
      const h = zoomHasta ? (agrupacion === "anual" ? zoomHasta.slice(0, 4) : zoomHasta) : null;
      return (!d || et >= d) && (!h || et <= h);
    };
    // Fuera lo que quedó sin datos por el filtro y, si la proyección está
    // apagada, los buckets 100% proyectados. El ACUMULADO se calcula sobre la
    // serie COMPLETA (desde el primer periodo con datos): el zoom Desde/Hasta
    // solo recorta lo que se PINTA, nunca el cálculo (corrección del usuario
    // 2026-09-01: con zoom desde abril el acumulado arrancaba en abril).
    let acum = 0;
    const conAcum = filas
      .filter((f) => (f.ventas_disp !== 0 || f.compras_disp !== 0)
        && (mostrarProy || !f.proyectado))
      .map((f) => {
        acum += f.ventas_disp - f.compras_disp;
        return { ...f, utilidad_acum: acum };
      });
    if (!acumular) return conAcum.filter((f) => enZoom(f.bucket as string));
    // Modo acumulado: ingresos/egresos (y la utilidad por periodo) pasan a
    // suma corrida. Las líneas dejan de cortarse en null (un corrido no se
    // interrumpe). La "Utilidad acumulada" del eje derecho ya era corrida.
    const keysAcum = [
      "ventas_real", "ventas_proy", "compras_real", "compras_proy",
      "ventas_disp", "compras_disp",
      ...gruposCp.map((g) => `${g.key}_disp`),
      "utilidad_real_linea", "utilidad_proy_linea", "utilidad_total_linea",
    ];
    const corrido: Record<string, number> = {};
    // El corrido también arranca desde el inicio de la serie completa; el zoom
    // solo decide qué periodos se pintan.
    return conAcum
      .map((f) => {
        const out: typeof f = { ...f };
        const reg = out as unknown as Record<string, number | null>;
        for (const k of keysAcum) {
          const v = typeof reg[k] === "number" ? (reg[k] as number) : 0;
          corrido[k] = (corrido[k] ?? 0) + v;
          reg[k] = corrido[k];
        }
        return out;
      })
      .filter((f) => enZoom(f.bucket as string));
  }, [q.data, mostrarProy, seriesVis, gruposCp, acumular, zoomDesde, zoomHasta, agrupacion]);

  // El total sigue al filtro de producto (igual que la gráfica).
  const productosVis = productos.filter((p) => activo(p.producto));
  const utilidadReal = productosVis.reduce((s, p) => s + p.utilidad_real, 0);
  const utilidadProy = productosVis.reduce((s, p) => s + p.utilidad_proy, 0);
  const utilidadTotal = mostrarProy ? utilidadReal + utilidadProy : utilidadReal;
  const hayParciales = productosVis.some((p) => !p.completo);

  // ---- tabla: filtros + orden ----
  // Desglose por periodo: agrega una columna por bucket (año o mes, según la
  // agrupación activa) con el monto del contrato en ese periodo.
  const [desglosar, setDesglosar] = useState(true); // "Desglose mensual" activo por default
  const periodosTabla = useMemo(() => buckets.map((b) => b.bucket as string), [buckets]);
  const [fFlujo, setFFlujo] = useState("");
  const [fProducto, setFProducto] = useState("");
  const [fContraparte, setFContraparte] = useState("");
  const [fCerteza, setFCerteza] = useState("");
  const [sortKey, setSortKey] = useState<string>("");
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  const toggleSort = (k: string) => {
    if (sortKey === k) setSortDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(k);
      setSortDir(1);
    }
  };
  const opcionesProducto = useMemo(
    () => Array.from(new Set(contratos.map((c) => c.producto))).sort(),
    [contratos],
  );
  const opcionesContraparte = useMemo(
    () => Array.from(new Set(contratos.map((c) => c.contraparte))).sort(),
    [contratos],
  );
  const filas = useMemo(() => {
    const certezaDe = (c: B2bUtilidadContrato) =>
      c.calculable ? (c.certeza ? "firme" : "proy") : "nocalc";
    let fs = contratos.filter(
      (c) =>
        (!fFlujo || c.lado_flujo === fFlujo) &&
        (!fProducto || c.producto === fProducto) &&
        (!fContraparte || c.contraparte === fContraparte) &&
        (!fCerteza || certezaDe(c) === fCerteza),
    );
    if (sortKey) {
      const val = (c: B2bUtilidadContrato): string | number | null => {
        switch (sortKey) {
          case "folio": return c.folio;
          case "contraparte": return c.contraparte;
          case "grupo": return c.grupo ?? "";
          case "producto": return c.producto;
          case "flujo": return c.lado_flujo;
          case "volumen": return c.volumen;
          case "total": return c.total;
          case "certeza": return c.calculable ? (c.certeza ? 2 : 1) : 0;
          default: return null;
        }
      };
      fs = [...fs].sort((a, b) => {
        const va = val(a);
        const vb = val(b);
        if (va == null && vb == null) return 0;
        if (va == null) return 1; // nulos siempre al final
        if (vb == null) return -1;
        const cmp =
          typeof va === "number" && typeof vb === "number"
            ? va - vb
            : String(va).localeCompare(String(vb), "es");
        return cmp * sortDir;
      });
    }
    return fs;
  }, [contratos, fFlujo, fProducto, fContraparte, fCerteza, sortKey, sortDir]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-slate-500" /> Utilidad B2B
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Utilidad bruta (ingresos − egresos) del libro calzado. <b>Real</b> = montos en
          firme; <b>proyección</b> = calculable pero sin certeza (provisionales al PNP o
          condición suspensiva). La energía muestra su pata de mercado MEM: la compra abona
          y la venta carga (liquidación PML×MWh).
        </p>
      </div>

      {/* ---- Controles ---- */}
      <div className="bg-white border rounded-lg px-4 py-3 space-y-2">
        <div className="flex flex-wrap items-center gap-5">
          <div className="flex gap-1">
            {(["anual", "mensual"] as const).map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setAgrupacion(a)}
                className={`text-sm border rounded px-3 py-1.5 capitalize ${
                  agrupacion === a ? "bg-blue-600 text-white border-blue-600" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {a}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            {(["USD", "MXN"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMoneda(m)}
                className={`text-sm border rounded px-3 py-1.5 ${
                  moneda === m ? "bg-slate-800 text-white border-slate-800" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          <label
            className="inline-flex items-center gap-1.5 text-sm text-slate-600"
            title="Tipo de cambio (MXN/USD) usado para convertir a la moneda de la vista; se aplica al salir del campo o con Enter. Vacío = último FIX de Banxico."
          >
            TC
            <input
              type="number"
              step="0.0001"
              min="0"
              className="border rounded px-2 py-1 text-sm w-24 tabular-nums"
              value={tcStr}
              onChange={(e) => setTcStr(e.target.value)}
              onBlur={aplicarTc}
              onKeyDown={(e) => {
                if (e.key === "Enter") aplicarTc();
              }}
            />
          </label>
          <span
            className="inline-flex items-center gap-1.5 text-sm text-slate-600"
            title="Solo entran los movimientos DESDE ese año/mes (KPIs, gráfica y detalle); vacío = todo el histórico"
          >
            Iniciar el corte en
            <input
              type="month"
              value={corteDesde}
              onChange={(e) => setCorteDesde(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
            {corteDesde && (
              <button
                type="button"
                className="text-[11px] text-blue-600 hover:underline"
                onClick={() => setCorteDesde("")}
              >
                limpiar
              </button>
            )}
          </span>
          {/* Grupo visual: el checkbox y su tope "Hasta:" son un solo control */}
          <div className="inline-flex items-center gap-3 border rounded-lg bg-slate-50 px-2.5 py-1">
            <label
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none"
              title="Suma las proyecciones de facturación cargadas en /proyecciones — solo en los meses donde aún no hay dato en firme (el real siempre manda)"
            >
              <input
                type="checkbox"
                checked={incluirProyecciones}
                onChange={(e) => setIncluirProyecciones(e.target.checked)}
                className="w-4 h-4 accent-slate-600 cursor-pointer"
              />
              Incluir Proyecciones
              <span className="text-[11px] text-slate-400 inline-flex items-center gap-1">
                (las cargadas en /proyecciones, marcadas con <IconoP title="Sale de /proyecciones" />, si no hay dato en firme)
              </span>
            </label>
            {incluirProyecciones && (
              <span
                className="inline-flex items-center gap-1.5 text-sm text-slate-600"
                title="Tope de la proyección: se recorta todo lo proyectado (cargadas y de ficha) después de este mes (vacío = sin tope)"
              >
                Hasta:
                <input
                  type="month"
                  className="border rounded px-2 py-1 text-sm bg-white"
                  value={proyHasta}
                  onChange={(e) => setProyHasta(e.target.value)}
                />
                {proyHasta && (
                  <button
                    type="button"
                    className="text-[11px] text-blue-600 hover:underline"
                    onClick={() => setProyHasta("")}
                  >
                    limpiar
                  </button>
                )}
              </span>
            )}
          </div>
          <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={acumular}
              onChange={(e) => setAcumular(e.target.checked)}
              className="w-4 h-4 accent-slate-600 cursor-pointer"
            />
            Acumular
            <span className="text-[11px] text-slate-400">(ingresos/egresos en suma corrida)</span>
          </label>
          {/* Fuente del movimiento: filtro de TODO el reporte (KPIs, gráfica,
              detalle y Excel); se recalcula en el backend (param `fuente`). */}
          <span
            className="inline-flex items-center gap-2 text-sm text-slate-600 border rounded-lg bg-slate-50 px-2.5 py-1"
            title="CENACE = liquidaciones que salen en el estado de cuenta (pata MEM / TBFin automática); Contraparte = movimientos fuera del estado de cuenta (strike, potencia, CEL, proyecciones). Combinables; deseleccionar ambos vuelve a mostrar todo. Afecta KPIs, gráfica, detalle y Excel."
          >
            <span className="text-slate-500">Mostrar transacciones con:</span>
            <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={verCenace}
                onChange={() => toggleFuente("cenace")}
                className="w-4 h-4 accent-slate-600 cursor-pointer"
              />
              CENACE
            </label>
            <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={verContraparte}
                onChange={() => toggleFuente("contraparte")}
                className="w-4 h-4 accent-slate-600 cursor-pointer"
              />
              Contraparte
            </label>
          </span>
          <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={porContraparte}
              onChange={(e) => setPorContraparte(e.target.checked)}
              className="w-4 h-4 accent-slate-600 cursor-pointer"
            />
            Por contraparte
            <span className="text-[11px] text-slate-400">(barras apiladas + líneas de totales)</span>
          </label>
          <label className="inline-flex items-center gap-1.5 text-sm text-slate-600">
            Back-to-Back
            <select
              className="border rounded px-2 py-1.5 text-sm max-w-64"
              value={grupo ?? ""}
              onChange={(e) => setGrupo(e.target.value ? Number(e.target.value) : null)}
              title="Limita el libro a un grupo back-to-back definido en /b2b"
            >
              <option value="">Todos los grupos</option>
              {grupos.map((g) => (
                <option key={g.id_grupo} value={g.id_grupo}>
                  {g.nombre} ({g.contratos.length})
                </option>
              ))}
            </select>
          </label>
          {q.isLoading && <span className="text-sm text-slate-400">Cargando…</span>}
        </div>
        {productosDisp.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500">Productos:</span>
            {productosDisp.map((p) => {
              const on = activo(p);
              const ui = PRODUCTO_UI[p] ?? { on: "bg-slate-700 text-white border-slate-700" };
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() =>
                    setProdExcl((prev) => {
                      const n = new Set(prev);
                      if (n.has(p)) n.delete(p);
                      else n.add(p);
                      return n;
                    })
                  }
                  title={on ? `Ocultar ${p} de la gráfica` : `Mostrar ${p}`}
                  className={`text-xs border rounded-full px-2.5 py-1 ${
                    on ? ui.on : "text-slate-400 border-slate-200 line-through"
                  }`}
                >
                  {p}
                </button>
              );
            })}
            {prodExcl.size > 0 && (
              <button
                type="button"
                onClick={() => setProdExcl(new Set())}
                className="text-xs text-slate-500 hover:text-slate-800 underline"
              >
                Ver todos
              </button>
            )}
          </div>
        )}
        {grupo != null && (
          <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1 inline-flex items-center gap-2">
            Mostrando solo el back-to-back{" "}
            <b>{grupos.find((g) => g.id_grupo === grupo)?.nombre ?? grupo}</b>
            <button
              type="button"
              onClick={() => setGrupo(null)}
              className="underline hover:text-blue-900"
            >
              quitar filtro
            </button>
          </div>
        )}
        {fx && (
          <div className="text-[11px] text-slate-400">
            Los montos en {moneda === "USD" ? "MXN" : "USD"} están <b>convertidos</b>
            {fx.fecha
              ? ` al FIX de Banxico del ${fx.fecha}`
              : " al TC capturado"}
            : <b>${fx.valor.toFixed(4)} MXN/USD</b> — marcados en la tabla de contratos.
          </div>
        )}
      </div>

      {q.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          No se pudo calcular la utilidad: {(q.error as Error).message}
        </div>
      )}

      {/* ---- KPIs ---- */}
      {!q.isLoading && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white border rounded-lg p-4">
            <div className="text-xs text-slate-500">
              Utilidad bruta {mostrarProy ? "total" : "en firme"} ({moneda})
            </div>
            <div className={`text-2xl font-semibold tabular-nums ${utilidadTotal >= 0 ? "text-emerald-700" : "text-red-700"}`}>
              {money(utilidadTotal, moneda)}
            </div>
            <div className="text-[11px] text-slate-400 mt-1">
              {mostrarProy ? (
                <>
                  en firme <b className="text-slate-500">{money(utilidadReal, moneda)}</b>
                  {" · "}
                  <span className="text-emerald-500/70">proy. {money(utilidadProy, moneda)}</span>
                </>
              ) : (
                "solo montos en firme (precio fijo o liquidación real)"
              )}
              {hayParciales && <span className="block">cifras parciales: hay contratos sin precio determinista</span>}
            </div>
          </div>
          {productos.map((p) => (
            <div
              key={p.producto}
              className={`bg-white border rounded-lg p-4 ${activo(p.producto) ? "" : "opacity-40"}`}
              title={activo(p.producto) ? undefined : "Oculto de la gráfica y del total"}
            >
              <div className="text-xs text-slate-500 flex items-center gap-1.5">
                {p.producto} ({moneda})
                {!p.completo && (
                  <span
                    className="text-[10px] font-medium bg-amber-100 text-amber-700 rounded-full px-1.5"
                    title={`${p.no_calculables} contrato(s) sin precio determinista — la cifra es parcial`}
                  >
                    parcial
                  </span>
                )}
              </div>
              <div className={`text-2xl font-semibold tabular-nums ${(mostrarProy ? p.utilidad : p.utilidad_real) >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                {money(mostrarProy ? p.utilidad : p.utilidad_real, moneda)}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {p.margen_ponderado != null && (
                  <>
                    margen pond.:{" "}
                    <b>
                      {money(p.margen_ponderado, moneda, p.producto === "Potencia" ? 0 : 2)}
                      {UNIDAD_MARGEN[p.producto] ?? ""}
                    </b>
                  </>
                )}
                {mostrarProy ? (
                  <span className="block text-slate-400">
                    en firme {money(p.utilidad_real, moneda)} ·{" "}
                    <span className="text-emerald-500/70">proy. {money(p.utilidad_proy, moneda)}</span>
                  </span>
                ) : (
                  <span className="block text-slate-400">
                    ingresos {money(p.ventas_real, moneda)} · egresos {money(p.compras_real, moneda)}
                  </span>
                )}
                {p.nota && <span className="block text-slate-400 italic">{p.nota}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---- Gráfica ---- */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="text-sm font-medium text-slate-700">
            Ingresos, egresos y utilidad {acumular ? "ACUMULADOS a" : "por"} cada{" "}
            {agrupacion === "anual" ? "año" : "mes"} ({moneda})
          </div>
          {/* Zoom SOLO de visualización (gráfica + columnas del desglose): no
              toca el cálculo — totales, KPIs y "Valor total" no cambian. */}
          <span
            className="inline-flex items-center gap-1.5 text-sm text-slate-600"
            title="Acota los periodos MOSTRADOS en la gráfica y en las columnas de la tabla; no modifica el cálculo ni los totales"
          >
            Desde
            <input
              type="month"
              className="border rounded px-2 py-1 text-sm"
              value={zoomDesde}
              onChange={(e) => setZoomDesde(e.target.value)}
            />
            Hasta
            <input
              type="month"
              className="border rounded px-2 py-1 text-sm"
              value={zoomHasta}
              onChange={(e) => setZoomHasta(e.target.value)}
            />
            {(zoomDesde || zoomHasta) && (
              <button
                type="button"
                className="text-[11px] text-blue-600 hover:underline"
                onClick={() => { setZoomDesde(""); setZoomHasta(""); }}
              >
                limpiar
              </button>
            )}
          </span>
        </div>
        {buckets.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">Sin montos calculables.</div>
        ) : (
          <div style={{ width: "100%", height: 380 }}>
            <ResponsiveContainer>
              <ComposedChart
                data={buckets}
                barGap={0}
                barSize={buckets.length > 150 ? Math.max(1, Math.floor(1500 / buckets.length / 2)) : undefined}
                margin={{ top: 8, right: 8, left: 8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={56} minTickGap={8} />
                <YAxis yAxisId="izq" tick={{ fontSize: 12 }} width={80} tickFormatter={(v) => nf.format(v as number)} />
                <YAxis
                  yAxisId="der"
                  orientation="right"
                  tick={{ fontSize: 12, fill: COLOR_ACUM }}
                  width={80}
                  tickFormatter={(v) => nf.format(v as number)}
                  label={{ value: "Acumulada", angle: 90, position: "insideRight", style: { fontSize: 11, fill: COLOR_ACUM } }}
                />
                <Tooltip formatter={(v: number, name: string) => [money(v, moneda), name]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {porContraparte ? (
                  <>
                    {gruposCp.map((g) => (
                      <Bar
                        key={g.key}
                        yAxisId="izq"
                        dataKey={`${g.key}_disp`}
                        stackId={g.lado}
                        fill={colorDe(g.contraparte)}
                        name={`${g.lado === "ventas" ? "Ingreso" : "Egreso"} · ${g.contraparte}`}
                      />
                    ))}
                    <Line
                      yAxisId="izq"
                      type="monotone"
                      dataKey="compras_disp"
                      name="Egresos totales"
                      stroke="#312e81"
                      strokeWidth={2.5}
                      dot={buckets.length <= 60 ? { r: 3 } : false}
                    />
                    <Line
                      yAxisId="izq"
                      type="monotone"
                      dataKey="ventas_disp"
                      name="Ingresos totales"
                      stroke="#92400e"
                      strokeWidth={2.5}
                      dot={buckets.length <= 60 ? { r: 3 } : false}
                    />
                  </>
                ) : (
                  <>
                    <Bar yAxisId="izq" dataKey="compras_real" stackId="c" name="Egresos (en firme)" fill={COLOR_EGRESOS} />
                    {mostrarProy && (
                      <Bar yAxisId="izq" dataKey="compras_proy" stackId="c" name="Egresos (proyección)" fill={COLOR_EGRESOS_PROY} radius={[3, 3, 0, 0]} />
                    )}
                    <Bar yAxisId="izq" dataKey="ventas_real" stackId="v" name="Ingresos (en firme)" fill={COLOR_INGRESOS} />
                    {mostrarProy && (
                      <Bar yAxisId="izq" dataKey="ventas_proy" stackId="v" name="Ingresos (proyección)" fill={COLOR_INGRESOS_PROY} radius={[3, 3, 0, 0]} />
                    )}
                    <Line
                      yAxisId="izq"
                      type="monotone"
                      dataKey="utilidad_real_linea"
                      name="Utilidad (en firme)"
                      stroke={COLOR_UTILIDAD}
                      strokeWidth={2.5}
                      dot={buckets.length <= 60 ? { r: 3 } : false}
                    />
                    {mostrarProy && (
                      <Line
                        yAxisId="izq"
                        type="monotone"
                        dataKey="utilidad_proy_linea"
                        name="Utilidad (proyección)"
                        stroke={COLOR_UTILIDAD_PROY}
                        strokeWidth={2}
                        strokeDasharray="6 3"
                        dot={false}
                      />
                    )}
                    {mostrarProy && (
                      // Línea INVISIBLE: solo agrega al tooltip el renglón
                      // "Utilidad (en firme + proyección)" (pedido 2026-09-01).
                      <Line
                        yAxisId="izq"
                        type="monotone"
                        dataKey="utilidad_total_linea"
                        name="Utilidad (en firme + proyección)"
                        stroke={COLOR_UTILIDAD}
                        strokeOpacity={0}
                        strokeWidth={0}
                        legendType="none"
                        dot={false}
                        activeDot={false}
                      />
                    )}
                  </>
                )}
                <Line
                  yAxisId="der"
                  type="monotone"
                  dataKey="utilidad_acum"
                  name="Utilidad acumulada (eje der.)"
                  stroke={COLOR_ACUM}
                  strokeWidth={2}
                  strokeDasharray="2 2"
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ---- Detalle por contrato ---- */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-slate-700">Detalle por contrato</span>
          <select
            value={fFlujo}
            onChange={(e) => setFFlujo(e.target.value)}
            className="border rounded px-2 py-1 text-xs text-slate-600 bg-white"
          >
            <option value="">Flujo: todos</option>
            <option value="ventas">Ingreso</option>
            <option value="compras">Egreso</option>
          </select>
          <select
            value={fProducto}
            onChange={(e) => setFProducto(e.target.value)}
            className="border rounded px-2 py-1 text-xs text-slate-600 bg-white"
          >
            <option value="">Producto: todos</option>
            {opcionesProducto.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <select
            value={fContraparte}
            onChange={(e) => setFContraparte(e.target.value)}
            className="border rounded px-2 py-1 text-xs text-slate-600 bg-white max-w-[16rem]"
          >
            <option value="">Contraparte: todas</option>
            {opcionesContraparte.map((cp) => (
              <option key={cp} value={cp}>{cp}</option>
            ))}
          </select>
          <select
            value={fCerteza}
            onChange={(e) => setFCerteza(e.target.value)}
            className="border rounded px-2 py-1 text-xs text-slate-600 bg-white"
          >
            <option value="">Certeza: todas</option>
            <option value="firme">En firme</option>
            <option value="proy">Proyección</option>
            <option value="nocalc">Sin monto</option>
          </select>
          {(fFlujo || fProducto || fContraparte || fCerteza) && (
            <button
              type="button"
              onClick={() => {
                setFFlujo("");
                setFProducto("");
                setFContraparte("");
                setFCerteza("");
              }}
              className="text-xs text-slate-500 hover:text-slate-800 underline"
            >
              Limpiar filtros
            </button>
          )}
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={desglosar}
              onChange={(e) => setDesglosar(e.target.checked)}
              className="w-3.5 h-3.5 accent-slate-600 cursor-pointer"
            />
            Desglose {agrupacion === "anual" ? "anual" : "mensual"}
            <span className="text-[10px] text-slate-400">
              (una columna por {agrupacion === "anual" ? "año" : "mes"}, como la gráfica)
            </span>
          </label>
          <button
            type="button"
            onClick={descargarXlsx}
            disabled={bajandoXlsx || contratos.length === 0}
            title="Descarga TODAS las patas del corte con el desglose por periodo; filtra en Excel con el autofiltro"
            className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 text-white rounded px-2.5 py-1 hover:bg-emerald-700 disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" />
            {bajandoXlsx ? "Generando…" : "Excel"}
          </button>
          <span className="ml-auto text-xs text-slate-400">
            {filas.length} de {contratos.length} filas
          </span>
        </div>
        {errorXlsx && (
          <div className="px-4 py-2 text-xs text-red-700 bg-red-50 border-t border-red-200">
            {errorXlsx}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b">
                <Th k="folio" label="Folio" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <Th k="contraparte" label="Contraparte / Grupo B2B" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <Th k="producto" label="Producto" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <Th k="flujo" label="Flujo" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <Th k="volumen" label="Volumen" derecha sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <Th k="total" label={`Valor total (${moneda})`} derecha sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                {desglosar &&
                  periodosTabla.map((p) => (
                    <th key={p} className="px-3 py-2 text-right whitespace-nowrap font-normal">
                      {p}
                    </th>
                  ))}
                <Th k="certeza" label="Certeza" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th className="px-3 py-2">Método / motivo</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((c) => (
                <FilaContrato
                  // OJO: folio+lado NO es único (las "(proyección cargada)" de un
                  // híbrido comparten ambos y difieren por producto/moneda); la key
                  // duplicada dejaba filas FANTASMA de otros contratos al filtrar.
                  key={`${c.folio}|${c.producto}|${c.lado_flujo}|${c.moneda_origen ?? ""}`}
                  c={c}
                  periodos={desglosar ? periodosTabla : null}
                  onVerMem={(cc, periodo) => setMemSel({ c: cc, periodo })}
                />
              ))}
              {filas.length === 0 && (
                <tr>
                  <td colSpan={8 + (desglosar ? periodosTabla.length : 0)} className="px-3 py-4 text-sm text-slate-400">
                    Ninguna fila coincide con los filtros.
                  </td>
                </tr>
              )}
            </tbody>
            {desglosar && filas.length > 0 && (
              <tfoot>
                {/* Total NETO: los ingresos suman y los egresos restan (= utilidad
                    de las filas visibles), en la columna Total y en cada periodo. */}
                <tr className="bg-slate-50 border-t font-medium text-xs">
                  <td className="px-3 py-2" colSpan={5}>
                    Total de las filas visibles (ingresos − egresos)
                  </td>
                  {(() => {
                    const signo = (c: B2bUtilidadContrato) =>
                      c.lado_flujo === "ventas" ? 1 : -1;
                    const neto = filas.reduce((s, c) => s + signo(c) * (c.total ?? 0), 0);
                    return (
                      <td
                        className={`px-3 py-2 text-right tabular-nums ${
                          neto < 0 ? "text-red-700" : "text-emerald-700"
                        }`}
                      >
                        {money(neto, moneda)}
                      </td>
                    );
                  })()}
                  {periodosTabla.map((p) => {
                    const neto = filas.reduce(
                      (s, c) =>
                        s + (c.lado_flujo === "ventas" ? 1 : -1) * (c.serie?.[p] ?? 0),
                      0,
                    );
                    return (
                      <td
                        key={p}
                        className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${
                          neto < 0 ? "text-red-700" : "text-emerald-700"
                        }`}
                      >
                        {money(neto, moneda)}
                      </td>
                    );
                  })}
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {noCalculables.length > 0 && (
          <div className="px-4 py-2 text-[11px] text-slate-400 border-t">
            {noCalculables.length} contrato{noCalculables.length === 1 ? "" : "s"} sin monto
            determinista (potencia MXN referida al PNP sin provisional; fichas CEL del
            pipeline sin precio). Se muestran atenuados con su motivo.
          </div>
        )}
      </div>

      {memSel && (
        <ModalMemDetalle
          sel={memSel.c}
          periodo={memSel.periodo}
          onClose={() => setMemSel(null)}
        />
      )}

      {/* ---- Cobranza vía TBF (CENACE): pagos/cobros del edocta que NO son
              liquidaciones diarias de energía. INFORMATIVO: es el cobro de
              facturas que el libro ya cuenta por calendario. ---- */}
      {(tbfQ.data?.items.length ?? 0) > 0 && (
        <div className="bg-white border rounded-lg">
          <div className="px-4 py-3 border-b flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-slate-700">Cobranza vía TBF (CENACE)</span>
            <span className="text-[11px] text-slate-400">
              pagos y cobros del estado de cuenta que liquidan facturas ya contadas por
              calendario (p. ej. la potencia de Blue) — informativo, NO suma a la utilidad
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 border-b">
                  <th className="px-4 py-2 text-left">Folio TBF</th>
                  <th className="px-4 py-2 text-left">Contraparte</th>
                  <th className="px-4 py-2 text-left">Dirección</th>
                  <th className="px-4 py-2 text-left">Operación</th>
                  <th className="px-4 py-2 text-left">Fecha de pago</th>
                  <th className="px-4 py-2 text-right">MWh</th>
                  <th className="px-4 py-2 text-right">Monto (MXN)</th>
                </tr>
              </thead>
              <tbody>
                {tbfQ.data!.items.map((t) => (
                  <tr key={t.folio + t.direccion} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-2 font-mono text-[12px]">{t.folio}</td>
                    <td className="px-4 py-2">{t.contraparte}</td>
                    <td className="px-4 py-2">
                      <span className={`text-[11px] font-medium rounded-full px-2 py-0.5 ${
                        t.direccion === "ingreso"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-rose-100 text-rose-700"
                      }`}>
                        {t.direccion === "ingreso" ? "Nos pagan" : "Pagamos"}
                      </span>
                    </td>
                    <td className="px-4 py-2">{t.fecha_oper}</td>
                    <td className="px-4 py-2">{t.fecha_pago ?? "—"}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{nf.format(t.mwh)}</td>
                    <td className={`px-4 py-2 text-right tabular-nums font-medium ${
                      t.direccion === "ingreso" ? "text-emerald-700" : "text-rose-700"
                    }`}>
                      {t.direccion === "ingreso" ? "+" : "−"}{money(t.monto_mxn, "MXN")}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 bg-slate-50">
                  <td colSpan={6} className="px-4 py-2 text-right text-xs font-medium text-slate-600">
                    Neto cobrado vía TBF
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold">
                    {money(
                      tbfQ.data!.items.reduce(
                        (s, t) => s + (t.direccion === "ingreso" ? t.monto_mxn : -t.monto_mxn), 0),
                      "MXN",
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Th({
  k,
  label,
  derecha,
  sortKey,
  sortDir,
  onSort,
}: {
  k: string;
  label: string;
  derecha?: boolean;
  sortKey: string;
  sortDir: 1 | -1;
  onSort: (k: string) => void;
}) {
  const activo = sortKey === k;
  return (
    <th
      className={`px-3 py-2 cursor-pointer select-none hover:text-slate-800 ${derecha ? "text-right" : ""}`}
      title="Ordenar por esta columna"
      onClick={() => onSort(k)}
    >
      <span className={`inline-flex items-center gap-0.5 ${activo ? "text-slate-800" : ""}`}>
        {label}
        {activo &&
          (sortDir === 1 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </span>
    </th>
  );
}

/** Modal: transacciones DIARIAS del MEM que suman el monto del periodo clicado
 *  (o toda la pata si no se clicó una columna). `periodo` = "2026-06" o "2026". */
function ModalMemDetalle({
  sel,
  periodo,
  onClose,
}: {
  sel: B2bUtilidadContrato;
  periodo?: string;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ["b2b-mem-detalle", sel.id_contrato],
    queryFn: () => getMemDetalle(sel.id_contrato),
  });
  // El endpoint regresa TODOS los días; aquí se filtran los del periodo clicado
  // (mes exacto "2026-06" o prefijo de año "2026").
  const items = (q.data?.items ?? []).filter(
    (it) => !periodo || (periodo.length === 7 ? it.mes === periodo : it.mes.startsWith(periodo)),
  );
  const d = q.data ? { ...q.data, items } : undefined;
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b flex items-center gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-800">
              {sel.folio} — transacciones del MEM ({d?.direccion ?? "…"})
              {periodo && (
                <span className="ml-2 text-[11px] font-medium bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">
                  {periodo}
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-400">
              PML × MWh por día de operación (motor TBFin / edocta); cada día cae en el
              mes de su FECHA DE PAGO del estado de cuenta. Montos en MXN.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-sm text-slate-500 hover:text-slate-900 border rounded px-2.5 py-1"
          >
            Cerrar ✕
          </button>
        </div>
        {q.isLoading && <div className="p-4 text-sm text-slate-500">Cargando detalle…</div>}
        {q.isError && (
          <div className="p-4 text-sm text-red-700">No se pudo cargar el detalle.</div>
        )}
        {d && (
          <div className="max-h-[65vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white shadow-sm">
                <tr className="text-xs text-slate-500 border-b">
                  <th className="px-4 py-2 text-left">Día de operación</th>
                  <th className="px-4 py-2 text-left">Fuente</th>
                  <th className="px-4 py-2 text-right">MWh</th>
                  <th className="px-4 py-2 text-right">Monto (MXN)</th>
                  <th className="px-4 py-2 text-left">Fecha de pago</th>
                  <th className="px-4 py-2 text-left">Cae en el mes</th>
                </tr>
              </thead>
              <tbody>
                {d.items.map((it, i) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-1.5 whitespace-nowrap">{it.fecha_operacion}</td>
                    <td className="px-4 py-1.5 text-xs text-slate-500">{it.fuente}</td>
                    <td className="px-4 py-1.5 text-right tabular-nums">{nf.format(it.mwh)}</td>
                    <td className="px-4 py-1.5 text-right tabular-nums">{money(it.monto_mxn, "MXN")}</td>
                    <td className="px-4 py-1.5 whitespace-nowrap">
                      {it.fecha_pago ?? "—"}
                      {it.pago_estimado && (
                        <span className="ml-1 text-xs text-amber-700" title="Día aún sin estado de cuenta: fecha estimada con el calendario de CENACE (cobra el miércoles de la 2ª semana siguiente, paga el de la 3ª)">(est.)</span>
                      )}
                    </td>
                    <td className="px-4 py-1.5 tabular-nums">{it.mes}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 bg-slate-50 font-medium">
                  <td className="px-4 py-2">{d.items.length} días</td>
                  <td />
                  <td className="px-4 py-2 text-right tabular-nums">
                    {nf.format(d.items.reduce((s, x) => s + x.mwh, 0))}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {money(d.items.reduce((s, x) => s + x.monto_mxn, 0), "MXN")}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function FilaContrato({
  c,
  periodos,
  onVerMem,
}: {
  c: B2bUtilidadContrato;
  /** columnas de desglose (buckets de la gráfica); null = sin desglose */
  periodos: string[] | null;
  /** abre el detalle diario de la pata MEM del periodo clicado (filas del MEM) */
  onVerMem?: (c: B2bUtilidadContrato, periodo?: string) => void;
}) {
  const esIngreso = c.lado_flujo === "ventas";
  const invertido = (c.rol_mpp === "vendedor") !== esIngreso;
  const esMem = !!c.metodo && c.metodo.includes("del MEM");
  // Folio en la primera línea; la descripción entre paréntesis de la pata
  // ("(strike)", "(potencia)", "(garantía)"…) en la segunda, en gris.
  const iParen = c.folio.indexOf(" (");
  const folioBase = iParen === -1 ? c.folio : c.folio.slice(0, iParen);
  const folioDesc = iParen === -1 ? null : c.folio.slice(iParen + 1);
  return (
    <tr className={`border-b last:border-0 ${c.calculable ? "" : "opacity-50"}`}>
      <td className="px-3 py-1.5 font-medium whitespace-nowrap">
        {folioBase}
        {folioDesc && (
          <span className="block text-xs font-normal text-slate-500">{folioDesc}</span>
        )}
      </td>
      <td className="px-3 py-1.5">
        {c.contraparte}
        <span className="text-xs text-slate-500"> / {c.grupo ?? "—"}</span>
      </td>
      <td className="px-3 py-1.5">{c.producto}</td>
      <td className="px-3 py-1.5 whitespace-nowrap">
        <span className={esIngreso ? "text-amber-700" : "text-indigo-700"}>
          {esIngreso ? "Ingreso" : "Egreso"}
        </span>
        <span className="text-[10px] text-slate-400 block">
          {c.rol_mpp === "vendedor" ? "Venta" : "Compra"}
          {invertido ? " · flujo MEM invertido" : ""}
        </span>
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">
        {c.volumen != null ? `${nf.format(c.volumen)} ${c.unidad_vol ?? ""}` : "—"}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums font-medium whitespace-nowrap">
        {c.de_proyecciones && (
          <IconoP className="w-3.5 h-4 mr-1" title="Valor tomado de /proyecciones (facturación proyectada cargada)" />
        )}
        {money(c.total, c.moneda)}
        {c.convertido && (
          <span
            className="ml-1.5 text-[10px] font-medium text-sky-700 bg-sky-100 rounded-full px-1.5 py-0.5 whitespace-nowrap"
            title={`Convertido de ${c.moneda_origen} al FIX (ver método)`}
          >
            Convertido
          </span>
        )}
      </td>
      {periodos?.map((p) => {
        const v = c.serie?.[p];
        // Proyección cargada: el monto es el RESIDUAL (plan − en firme);
        // el tooltip enseña el plan completo del periodo.
        const plan = c.serie_plan?.[p];
        const titulo =
          v && plan != null
            ? `Proyectado total del periodo: ${money(plan, c.moneda)}` +
              (Math.abs(plan - v) > 0.005
                ? ` · ya en firme: ${money(plan - v, c.moneda)} · residual mostrado: ${money(v, c.moneda)}`
                : " (sin facturas aún: se muestra completo)")
            : undefined;
        // Efecto en caja del movimiento: verde tenue = SUMA dinero (ingreso
        // positivo o egreso negativo), rojo tenue = RESTA (egreso positivo o
        // ingreso negativo). Sin monto, sin fondo.
        const suma = v != null && Math.abs(v) > 0.005 && (esIngreso ? v > 0 : v < 0);
        const resta = v != null && Math.abs(v) > 0.005 && (esIngreso ? v < 0 : v > 0);
        return (
          <td
            key={p}
            title={titulo}
            className={`px-3 py-1.5 text-right tabular-nums whitespace-nowrap ${
              v ? "" : "text-slate-300"
            } ${suma ? "bg-emerald-50" : resta ? "bg-red-50" : ""} ${
              titulo ? "cursor-help underline decoration-dotted decoration-slate-300 underline-offset-2" : ""}`}
          >
            {v && esMem && onVerMem ? (
              <button
                type="button"
                onClick={() => onVerMem(c, p)}
                title={`Ver las transacciones del MEM que suman ${p}`}
                className="text-blue-700 hover:underline tabular-nums"
              >
                {money(v, c.moneda)}
              </button>
            ) : v ? (
              <>
                {c.de_proyecciones && (
                  <IconoP className="w-3 h-3.5 mr-1" title="Valor tomado de /proyecciones" />
                )}
                {money(v, c.moneda)}
              </>
            ) : (
              "—"
            )}
          </td>
        );
      })}
      <td className="px-3 py-1.5">
        {c.calculable ? (
          c.certeza ? (
            <span className="text-[11px] font-medium text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5 whitespace-nowrap">
              En firme
            </span>
          ) : (
            <span className="text-[11px] font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 whitespace-nowrap">
              Proyección
            </span>
          )
        ) : (
          <span className="text-[11px] text-slate-400">—</span>
        )}
      </td>
      <td className="px-3 py-1.5 text-xs text-slate-500">{c.metodo ?? c.motivo ?? "—"}</td>
    </tr>
  );
}

/** Icono "P" (domo azul con P blanca sobre base metálica): marca los montos
 *  que salen de las proyecciones cargadas en /proyecciones. */
function IconoP({ className = "w-3.5 h-4", title }: { className?: string; title?: string }) {
  const id = useId().replace(/:/g, "");
  return (
    <svg viewBox="0 0 20 23" className={`inline-block align-[-2px] shrink-0 ${className}`} role="img" aria-label={title ?? "Proyección"}>
      {title && <title>{title}</title>}
      <defs>
        <linearGradient id={`${id}d`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#7dd3fc" />
          <stop offset="0.45" stopColor="#0ea5e9" />
          <stop offset="1" stopColor="#075985" />
        </linearGradient>
        <linearGradient id={`${id}b`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f1f5f9" />
          <stop offset="0.5" stopColor="#94a3b8" />
          <stop offset="1" stopColor="#475569" />
        </linearGradient>
      </defs>
      <path d="M2.5 18 V9.5 a7.5 7.5 0 0 1 15 0 V18 Z" fill={`url(#${id}d)`} />
      <text x="10.6" y="15.6" textAnchor="middle" fontSize="11.5" fontWeight="900" fontFamily="Arial, sans-serif" fill="#0c4a6e">P</text>
      <text x="10" y="15" textAnchor="middle" fontSize="11.5" fontWeight="900" fontFamily="Arial, sans-serif" fill="#f8fafc">P</text>
      <rect x="1" y="17.5" width="18" height="4.5" rx="1.2" fill={`url(#${id}b)`} />
    </svg>
  );
}
